import React, { useContext, useEffect } from 'react';
import Navigation from './header-and-footer/header/Navigation';
import { Footer } from './header-and-footer/footer/Footer';
import { SkipToContent } from '@entur/a11y';
import { FormattedMessage } from 'react-intl';
import { createMedia } from '@artsy/fresnel';
import {
  CONSENT_UPDATED_EVENT,
  ConsentDetails,
  Consents,
  formatConsentEvent,
  waitFor,
} from '../utils/cmpUtils';

import { breakpoints } from '../styles/breakpoints';
import '../styles/OmturGrid.scss';

export const { MediaContextProvider, Media } = createMedia({
  breakpoints: {
    mobile: 0,
    desktop: breakpoints.laptop,
    largeDesktop: breakpoints.desktop,
  },
});

type LayoutProps = {
  children: React.ReactNode;
  isContrast?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

const Layout = ({ children, isContrast = false, style }: LayoutProps) => {
  useEffect(() => {
    let previousConsents: Consents | null = null;
    const POSTHOG_SERVICE_NAME = 'PostHog.com';

    async function handleConsentUpdate(
      event: Event & { detail?: ConsentDetails }
    ) {
      if (typeof window === 'undefined') return;

      const consents = formatConsentEvent(event);
      const posthogConsent = consents?.find(
        (c) => c.name === POSTHOG_SERVICE_NAME
      );

      if (posthogConsent?.consentGiven) {
        await waitFor(() => window.posthog !== undefined);
        // @ts-expect-error identify does exist on posthog
        window.posthog?.identify(event.detail?.consent.controllerId);
        // @ts-expect-error opt_in_capturing does exist on posthog
        window.posthog?.opt_in_capturing();
      }

      if (previousConsents !== null) {
        const previousPostHogConsent = previousConsents?.find(
          (c) => c.name === POSTHOG_SERVICE_NAME
        );

        const posthogDeclined =
          previousPostHogConsent?.consentGiven === true &&
          posthogConsent?.consentGiven === false;

        if (posthogDeclined) location.reload();
      }

      previousConsents = consents;
    }

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);

    return () =>
      window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);
  }, []);

  return (
    <MediaContextProvider>
      <SkipToContent>
        <FormattedMessage id="skipToContent" />
      </SkipToContent>
      <Navigation isContrast={isContrast} />

      <main className="omtur-grid" id="main-content" style={style}>
        {children}
      </main>

      <Footer />
    </MediaContextProvider>
  );
};

export const LayoutWithoutGrid = ({
  children,
  isContrast = false,
}: LayoutProps) => {
  React.useMemo(() => {}, []);

  return (
    <MediaContextProvider>
      <SkipToContent>
        <FormattedMessage id="skipToContent" />
      </SkipToContent>
      <Navigation isContrast={isContrast} />
      {children}
      <Footer />
    </MediaContextProvider>
  );
};

export default Layout;
